import { queryOptions } from "@tanstack/react-query";
import { apiGet } from "@softmax/observatory-client/client";
import type {
  DivisionLeaderboardSummaryPublic,
  Schemas,
} from "@softmax/observatory-client/types";

import {
  getBrowseCatalog,
  getBrowseLeaderboard,
  getBrowseThumbnails,
} from "./browse.functions";
import type { CoworldRow } from "./catalog/browse-rows";

export function browseCatalogQueryOptions(href: string) {
  return queryOptions({
    queryKey: ["public", "browse", "catalog", href],
    queryFn: async ({ signal }) => await getBrowseCatalog({ href, signal }),
    staleTime: 60_000,
  });
}

export function browseThumbnailsQueryOptions(leagueIds: string[]) {
  const sortedIds = leagueIds.toSorted();
  return queryOptions({
    queryKey: ["public", "browse", "thumbnails", sortedIds],
    queryFn: ({ signal }) =>
      getBrowseThumbnails({ leagueIds: sortedIds, signal }),
    staleTime: 60_000,
    enabled: sortedIds.length > 0,
  });
}

export function browseContinuationsQueryOptions({
  viewerId,
  rows,
}: {
  viewerId: string | null;
  rows: CoworldRow[];
}) {
  const parameters = new URLSearchParams();

  for (const row of rows.toSorted((a, b) => a.key.localeCompare(b.key))) {
    parameters.append("game_id", row.key);
  }

  const query = parameters.toString();
  return queryOptions({
    queryKey: ["viewer", viewerId, "browse", "continuations", query],
    queryFn: ({ signal }) =>
      apiGet<Schemas["ListedGameContinuation"][]>(
        `/v2/leagues/catalog/games/continuations?${query}`,
        { elevatedPrivileges: false, signal },
      ),
    enabled: viewerId !== null && rows.length > 0,
    staleTime: 30_000,
  });
}

export function browseCountsQueryOptions({
  viewerId,
  leagueId,
  divisionId,
}: {
  viewerId: string | null;
  leagueId: string;
  divisionId: string | null;
}) {
  return queryOptions({
    queryKey: [
      divisionId === null ? "public" : "viewer",
      divisionId === null ? null : viewerId,
      "browse",
      "counts",
      leagueId,
      divisionId,
    ],
    queryFn: async ({ signal }) => {
      if (divisionId === null)
        return getBrowseLeaderboard({ leagueId, signal });

      const board = await apiGet<DivisionLeaderboardSummaryPublic | null>(
        `/v2/divisions/${encodeURIComponent(divisionId)}/leaderboard/summary`,
        { elevatedPrivileges: false, signal },
      );
      return (
        board && {
          players: board.entries.length,
          policies: new Set(
            board.entries.flatMap((entry) =>
              entry.policy_label ? [entry.policy_label] : [],
            ),
          ).size,
        }
      );
    },
    staleTime: 60_000,
  });
}
